/**
 * Hook to add scroll-reveal animations to landing sections.
 *
 * What: Uses Intersection Observer to trigger CSS transitions when sections scroll into view.
 *       Assigns `data-reveal` attributes (via landingRevealStyles) and `--stagger` / `--card-stagger`
 *       CSS custom properties to children for cascading entrances.
 * Why: Progressive disclosure guides attention down the page. Stagger gives physical weight.
 * When: Update when changing visibility thresholds or the stagger indexing strategy.
 */
import { useLayoutEffect } from 'react';

import { getRevealAttr } from '@/components/landing/landingRevealStyles';

declare global {
  interface Window {
    __PRERENDER_CAPTURE__?: boolean;
  }
}

interface UseSectionFadeInOptions {
  threshold?: number;
  rootMargin?: string;
}

/**
 * Assign `--stagger` to direct children and `--card-stagger` to nested Paper cards.
 */
function assignStaggerIndices(el: HTMLElement): void {
  const wrapper =
    el.querySelector(':scope > .MuiContainer-root') ||
    el.querySelector(':scope > .MuiStack-root');
  if (!wrapper) return;

  // Top-level children (eyebrow, title, body, card row, etc.)
  const children = wrapper.children;
  for (let i = 0; i < children.length; i++) {
    (children[i] as HTMLElement).style.setProperty('--stagger', String(i));
  }

  // Card-level: Paper cards inside nested flex/Stack rows
  const cards = wrapper.querySelectorAll(
    ':scope > .MuiStack-root > .MuiPaper-root, :scope > .MuiBox-root > .MuiPaper-root'
  );
  cards.forEach((card, i) => {
    (card as HTMLElement).style.setProperty('--card-stagger', String(i));
  });
}

/**
 * Attach scroll-reveal classes and stagger indices to landing sections.
 *
 * Sections start hidden (via `.section-fade`) and reveal when they intersect
 * the viewport (via `.section-visible`). The reveal style is determined by
 * `data-reveal`, which is resolved from the section's ID through the
 * design-system mapping in `landingRevealStyles.ts`.
 *
 * **Height ceiling.** The default `threshold` of 0.1 asks for a tenth of the
 * section to be on screen at once, so a section taller than ~10 viewports can
 * never satisfy it — it is hidden on mount, never observed as intersecting,
 * and stays at `opacity: 0` for good. Don't register a section that grows with
 * its data (the school directory is the live example, at ~5,000px on a level
 * page); if one genuinely needs to reveal, pass `threshold: 0` so the top edge
 * crossing `rootMargin` is enough.
 */
export function useSectionFadeIn(
  sectionIds: string[],
  options: UseSectionFadeInOptions = {}
): void {
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.__PRERENDER_CAPTURE__ === true) return undefined;

    const threshold = options.threshold ?? 0.1;
    // On mobile (< 900px), use a smaller bottom offset so sections trigger
    // earlier — the smaller viewport means less room before content scrolls past.
    const isMobile = window.innerWidth < 900;
    const rootMargin =
      options.rootMargin ??
      (isMobile ? '0px 0px -30px 0px' : '0px 0px -60px 0px');

    const elements = sectionIds
      .map(id => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return undefined;

    if (!('IntersectionObserver' in window)) {
      elements.forEach(el => el.classList.add('section-visible'));
      return undefined;
    }

    // Measure every section BEFORE any class/style writes: interleaving
    // getBoundingClientRect with classList mutations forces one synchronous
    // reflow per section (flagged by Lighthouse). Reading first costs a
    // single layout pass, and measures the resting position before
    // `.section-fade` applies its hidden-state transform.
    const viewportHeight = window.innerHeight || 0;
    const rects = elements.map(el => el.getBoundingClientRect());

    // Set up each section: baseline class, reveal flavor, stagger indices,
    // and reveal anything already in view immediately (no blank flash).
    elements.forEach((el, i) => {
      el.classList.add('section-fade');
      el.setAttribute('data-reveal', getRevealAttr(el.id));
      assignStaggerIndices(el);

      const rect = rects[i];
      if (rect.bottom > 0 && rect.top < Math.max(viewportHeight * 0.9, 1)) {
        el.classList.add('section-visible');
      }
    });

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('section-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    elements.forEach(el => observer.observe(el));

    // A section reached by an in-page anchor must not animate. The reader is
    // already looking at it, and the browser scrolls to a position computed
    // while the section still carries its hidden `translateY` — so playing the
    // entrance walks the content ~20px out from under where they just landed.
    const revealInstantly = (id: string) => {
      const target = elements.find(el => el.id === id);
      if (!target) return;
      target.classList.add('section-instant', 'section-visible');
      observer.unobserve(target);
    };

    // Capture phase, so the transform is already gone by the time the browser
    // computes where to scroll. `hashchange` is too late for that — it fires
    // after the scroll — so it only serves back/forward and external deep
    // links, where cancelling the animation is still the win.
    const onAnchorClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.(
        'a[href^="#"]'
      );
      const hash = anchor?.getAttribute('href');
      if (hash && hash.length > 1) revealInstantly(hash.slice(1));
    };
    const onHashChange = () => revealInstantly(window.location.hash.slice(1));

    onHashChange();
    document.addEventListener('click', onAnchorClick, true);
    window.addEventListener('hashchange', onHashChange);

    return () => {
      document.removeEventListener('click', onAnchorClick, true);
      window.removeEventListener('hashchange', onHashChange);
      observer.disconnect();
    };
  }, [sectionIds, options.threshold, options.rootMargin]);
}
